import React from 'react';
import {
	type AutocompleteRenderInputParams,
	CircularProgress,
	TextField,
	type TextFieldProps,
} from '@mui/material';
import {styled} from '@netx/core-components/Theme';

const SelectFieldInputCircularProgress = styled(CircularProgress)(
	({theme}) => ({
		position: 'absolute',
		right: 11,
		top: 'calc(50% - 10px)',
	}),
);

export interface VirtualizedInputProps
	extends Omit<
			Partial<AutocompleteRenderInputParams>,
			'inputProps' | 'InputLabelProps' | 'InputProps' | 'size'
		>,
		Omit<TextFieldProps, 'classes' | 'error' | 'label'> {
	error?: string;
	label?: string | React.ReactNode;
	loading?: boolean;
	readOnly?: boolean;
}

/**
 * Input for virtualized select field
 */
export const SelectFieldInputPrimitive = React.forwardRef<
	HTMLDivElement,
	VirtualizedInputProps
>(({error, loading, readOnly, InputProps, ...props}, ref) => {
	return (
		<TextField
			ref={ref}
			error={!!error}
			fullWidth
			{...props}
			inputProps={{
				'aria-haspopup': 'true',
				'aria-labelledby': props.InputLabelProps?.id,
				autoComplete: 'off',
				...props.inputProps,
				// TODO: do we need to be overriding this here?
				tabIndex: 0,
			}}
			InputProps={{
				role: 'listbox',
				'aria-labelledby': props.InputLabelProps?.id,
				'aria-multiselectable': true,
				...InputProps,
				endAdornment: (
					<>
						{loading ? (
							<SelectFieldInputCircularProgress color="primary" size={20} />
						) : null}
						{readOnly ? null : InputProps?.endAdornment}
					</>
				),
			}}
		/>
	);
});

export const SelectFieldInput = SelectFieldInputPrimitive;
